import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { ScanFace, Loader2, Check, X, ChevronDown, Users, ImageIcon } from 'lucide-react';
import { toast } from '../utils/toast';

const API = `${window.__API_BASE}/api`;

// Global Face Lock kill-switch (FACELOCK_ENABLED=0 backend-side) — fetched
// once per page load and shared by all Face Lock controls.
let _flEnabledCache = null;
async function faceLockEnabled() {
  if (_flEnabledCache !== null) return _flEnabledCache;
  try {
    const r = await axios.get(`${API}/face-lock/config`);
    _flEnabledCache = r.data?.enabled !== false;
  } catch (_) {
    _flEnabledCache = true;
  }
  return _flEnabledCache;
}

const resolveUrl = (url) => !url ? '' : url.startsWith('http') ? url : `${window.__API_BASE}${url}`;

const activeKeyframeUrl = (shot) =>
  shot.keyframe_hyperreal_url || shot.keyframe_url || shot.keyframe_s3_url || shot.keyframe_plus_url;

// ═══════════ Per-shot Face Lock button (Keyframe + Clip cards) ═══════════
// States: idle → queued/processing (spinner + poll) → review (compare modal)
// Gate: disabled when the shot has no cast binding (spec §3.1).
export function FaceLockButton({ shot, media, onReload }) {
  const boundCast = ((shot.asset_bible_spec?.cast_bindings) || []).filter(b => b.cast_id);
  const forThisMedia = shot.face_lock_media === media;
  const [status, setStatus] = useState(forThisMedia ? (shot.face_lock_status || 'idle') : 'idle');
  const [error, setError] = useState(forThisMedia ? shot.face_lock_error : null);
  const [lockedUrl, setLockedUrl] = useState(
    forThisMedia ? (media === 'keyframe' ? shot.keyframe_facelocked_url : shot.video_facelocked_url) : null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flEnabled, setFlEnabled] = useState(_flEnabledCache !== false);
  const pollRef = useRef(null);

  useEffect(() => { faceLockEnabled().then(setFlEnabled); }, []);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const startPoll = () => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/shots/${shot.id}/face-lock-status`);
        const d = res.data || {};
        if (d.media !== media) return;
        if (d.status === 'review') {
          stopPoll();
          setStatus('review');
          setLockedUrl(media === 'keyframe' ? d.keyframe_facelocked_url : d.video_facelocked_url);
          toast.success('Face Lock ready — review before/after');
        } else if (d.status === 'failed') {
          stopPoll();
          setStatus('failed');
          setError(d.error);
          toast.error(d.error || 'Face Lock failed');
        } else {
          setStatus(d.status);
        }
      } catch (_) { /* keep polling */ }
    }, 4000);
  };

  useEffect(() => {
    if (status === 'queued' || status === 'processing') startPoll();
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fire = async (castIds) => {
    setMenuOpen(false);
    try {
      await axios.post(`${API}/shots/${shot.id}/face-lock`, { media, cast_ids: castIds || null });
      setStatus('queued');
      setError(null);
      toast.info('Face Lock queued — snapping identity to the Asset Bible cast…');
      startPoll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Face Lock failed to start');
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/shots/${shot.id}/face-lock/accept`);
      toast.success(res.data?.video_stale
        ? 'Face Lock accepted — video for this shot is now stale'
        : 'Face Lock accepted');
      setStatus('accepted');
      setShowReview(false);
      await onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Accept failed');
    } finally { setBusy(false); }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/shots/${shot.id}/face-lock/discard`);
      toast.info('Face Lock discarded — original untouched');
      setStatus('idle');
      setLockedUrl(null);
      setShowReview(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Discard failed');
    } finally { setBusy(false); }
  };

  const noCast = boundCast.length === 0;
  const inFlight = status === 'queued' || status === 'processing';

  const btnBase = 'relative group p-1 rounded-md flex items-center justify-center shadow-md transition-all backdrop-blur-sm';
  const tooltip = (text) => (
    <span className="pointer-events-none absolute bottom-full right-0 mb-1.5 px-2 py-1 rounded-md bg-black/90 border border-white/10 text-white/90 text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
      {text}
    </span>
  );

  if (!flEnabled) {
    return (
      <button disabled className={`${btnBase} bg-black/40 text-white/25 border border-white/10 cursor-not-allowed grayscale`}
        data-testid={`face-lock-btn-${media}-${shot.id}`}>
        <ScanFace className="w-3 h-3" />
        {tooltip('Face Lock is temporarily disabled')}
      </button>
    );
  }

  if (noCast) {
    return (
      <button disabled className={`${btnBase} bg-black/40 text-white/30 border border-white/10 cursor-not-allowed`}
        data-testid={`face-lock-btn-${media}-${shot.id}`}>
        <ScanFace className="w-3 h-3" />
        {tooltip('Bind cast for this shot first.')}
      </button>
    );
  }

  if (inFlight) {
    return (
      <button disabled className={`${btnBase} bg-sky-500/20 text-sky-300 animate-pulse cursor-wait border border-sky-400/40`}
        data-testid={`face-lock-btn-${media}-${shot.id}`}>
        <Loader2 className="w-3 h-3 animate-spin" />
        {tooltip(status === 'queued' ? 'Face Lock queued — waiting for GPU worker' : 'Face Lock processing…')}
      </button>
    );
  }

  if (status === 'review') {
    return (
      <>
        <button onClick={(e) => { e.stopPropagation(); setShowReview(true); }}
          className={`${btnBase} bg-sky-500/30 text-sky-100 hover:bg-sky-500/50 border border-sky-400/70 active:scale-95`}
          data-testid={`face-lock-btn-${media}-${shot.id}`}>
          <ScanFace className="w-3 h-3" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-sky-400 animate-pulse" />
          {tooltip('Face Lock ready — click to review')}
        </button>
        {showReview && (
          <FaceLockReviewModal
            shot={shot} media={media} lockedUrl={lockedUrl} busy={busy}
            onAccept={accept} onDiscard={discard} onClose={() => setShowReview(false)}
          />
        )}
      </>
    );
  }

  // idle / accepted / failed → fire (multi-cast opens dropdown)
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => (boundCast.length > 1 ? setMenuOpen(v => !v) : fire(null))}
        className={`${btnBase} active:scale-95 ${
          status === 'failed'
            ? 'bg-rose-500/25 text-rose-300 hover:bg-rose-500/40 border border-rose-400/50'
            : 'bg-black/60 text-sky-300 hover:bg-sky-500/30 border border-sky-400/40'
        }`}
        data-testid={`face-lock-btn-${media}-${shot.id}`}
      >
        <ScanFace className="w-3 h-3" />
        {boundCast.length > 1 && <ChevronDown className="w-2 h-2 ml-px" />}
        {tooltip(status === 'failed' ? `Face Lock failed: ${error || 'retry'}` : 'Face Lock — snap identity to cast')}
      </button>
      {menuOpen && (
        <div className="absolute bottom-full right-0 mb-1.5 z-50 min-w-[150px] rounded-lg bg-[#141418] border border-white/15 shadow-xl overflow-hidden"
          data-testid={`face-lock-cast-menu-${shot.id}`}>
          {boundCast.map(b => (
            <button key={b.cast_id} onClick={() => fire([b.cast_id])}
              className="w-full px-3 py-1.5 text-left text-[11px] text-white/85 hover:bg-sky-500/20 flex items-center gap-1.5"
              data-testid={`face-lock-cast-option-${b.cast_id}`}>
              <ScanFace className="w-3 h-3 text-sky-400" /> {b.cast_name || 'Cast'}
            </button>
          ))}
          <button onClick={() => fire(null)}
            className="w-full px-3 py-1.5 text-left text-[11px] text-sky-300 hover:bg-sky-500/20 border-t border-white/10 flex items-center gap-1.5 font-medium"
            data-testid={`face-lock-cast-option-all-${shot.id}`}>
            <Users className="w-3 h-3" /> All bound cast
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════ Review modal — slider (keyframe) / side-by-side (clip) ═══════════
export function FaceLockReviewModal({ shot, media, lockedUrl, busy, onAccept, onDiscard, onClose }) {
  const [slider, setSlider] = useState(50);
  const originalUrl = media === 'keyframe'
    ? activeKeyframeUrl(shot)
    : (shot.video_s3_url || shot.video_url);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="face-lock-review-modal">
      <div className="bg-[#101014] border border-white/15 rounded-2xl max-w-4xl w-full p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ScanFace className="w-4 h-4 text-sky-400" />
            <h3 className="text-sm font-semibold text-white">Face Lock — Review {media === 'keyframe' ? 'Keyframe' : 'Clip'}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60"
            data-testid="face-lock-review-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {media === 'keyframe' ? (
          <div className="relative w-full rounded-xl overflow-hidden bg-black select-none" style={{ aspectRatio: '16/9' }}>
            <img src={resolveUrl(originalUrl)} alt="Original" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
            <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - slider}% 0 0)` }}>
              <img src={resolveUrl(lockedUrl)} alt="Face Locked" className="w-full h-full object-contain" draggable={false} />
            </div>
            <div className="absolute top-0 bottom-0 w-px bg-sky-400 pointer-events-none" style={{ left: `${slider}%` }} />
            <input type="range" min="0" max="100" value={slider}
              onChange={(e) => setSlider(Number(e.target.value))}
              className="absolute inset-x-0 bottom-2 mx-6 accent-sky-400 cursor-ew-resize"
              data-testid="face-lock-compare-slider" />
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/70 text-[10px] text-white/80 pointer-events-none">Face Locked</span>
            <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/70 text-[10px] text-white/80 pointer-events-none">Original</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="block text-[10px] text-white/50 mb-1.5 uppercase tracking-wider">Original</span>
              <video src={resolveUrl(originalUrl)} controls className="w-full rounded-xl bg-black" data-testid="face-lock-video-original" />
            </div>
            <div>
              <span className="block text-[10px] text-sky-300 mb-1.5 uppercase tracking-wider">Face Locked</span>
              <video src={resolveUrl(lockedUrl)} controls className="w-full rounded-xl bg-black border border-sky-400/40" data-testid="face-lock-video-locked" />
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onDiscard} disabled={busy}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-300 hover:bg-rose-500/30 border border-rose-500/30 transition disabled:opacity-50 flex items-center gap-1.5"
            data-testid="face-lock-discard-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Discard
          </button>
          <button onClick={onAccept} disabled={busy}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-black hover:bg-emerald-400 transition disabled:opacity-50 flex items-center gap-1.5"
            data-testid="face-lock-accept-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accept
          </button>
        </div>
        {media === 'keyframe' && (
          <p className="text-[10px] text-white/40 mt-2">
            Accept replaces the working keyframe and marks this shot's video stale (regenerate to match).
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}

// ═══════════ Stage-level "Face Lock All" batch button (spec §3.2) ═══════════
export function FaceLockAllButton({ projectId, media, onReload }) {
  const [running, setRunning] = useState(false);
  const [flEnabled, setFlEnabled] = useState(_flEnabledCache !== false);
  useEffect(() => { faceLockEnabled().then(setFlEnabled); }, []);
  const fire = async (e) => {
    e.stopPropagation();
    setRunning(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/face-lock-all`, { media });
      const d = res.data || {};
      if (d.queued > 0) {
        toast.success(`Face Lock queued on ${d.queued} shot${d.queued === 1 ? '' : 's'}${d.skipped_no_cast ? ` · ${d.skipped_no_cast} skipped (no cast bound)` : ''}`);
        await onReload?.();
      } else {
        toast.info(`No shots queued — ${d.skipped_no_cast || 0} without cast bindings, ${d.skipped_no_media || 0} without a ${media}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Face Lock All failed');
    } finally { setRunning(false); }
  };
  return (
    <button onClick={fire} disabled={running || !flEnabled}
      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition disabled:opacity-50 whitespace-nowrap ${
        flEnabled ? 'bg-sky-500/10 text-sky-300 hover:bg-sky-500/25 border-sky-400/30' : 'bg-white/5 text-white/25 border-white/10 grayscale cursor-not-allowed'
      }`}
      title={flEnabled
        ? `Run Face Lock on every ${media === 'keyframe' ? 'keyframe' : 'clip'} with cast bindings — results land in per-shot review`
        : 'Face Lock is temporarily disabled'}
      data-testid={`face-lock-all-btn-${media}`}>
      {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanFace className="w-3.5 h-3.5" />}
      Face Lock All
    </button>
  );
}

// ═══════════ Asset Bible cast card — canonical face ref override (spec §4.3) ═══════════
export function CastFaceRefControl({ projectId, candidate }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(candidate.face_ref_url || '');
  const [saving, setSaving] = useState(false);
  const [flEnabled, setFlEnabled] = useState(_flEnabledCache !== false);
  useEffect(() => { faceLockEnabled().then(setFlEnabled); }, []);
  const hasOverride = !!candidate.face_ref_url;

  if (!flEnabled) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border bg-white/5 text-white/25 border-white/10 grayscale cursor-not-allowed"
        title="Face Lock is temporarily disabled"
        data-testid={`cast-face-ref-btn-${candidate.id}`}>
        <ScanFace className="w-2.5 h-2.5" /> Face Ref
      </span>
    );
  }

  const save = async (newUrl) => {
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/asset-bible/cast/${candidate.id}/face-ref`, { url: newUrl || null });
      candidate.face_ref_url = newUrl || undefined;
      setUrl(newUrl || '');
      toast.success(newUrl ? 'Face ref updated' : 'Face ref reset to hero image');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Face ref update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border transition ${
          hasOverride ? 'bg-sky-500/15 text-sky-300 border-sky-400/40' : 'bg-white/5 text-white/50 border-white/10 hover:text-white/80'
        }`}
        title="Canonical face reference for Face Lock (defaults to this card's hero image)"
        data-testid={`cast-face-ref-btn-${candidate.id}`}>
        <ScanFace className="w-2.5 h-2.5" /> Face Ref{hasOverride && ' ✓'}
      </button>
      {open && (
        <div className="absolute z-50 bottom-full left-0 mb-1.5 w-56 rounded-xl bg-[#141418] border border-white/15 shadow-xl p-3"
          data-testid={`cast-face-ref-popover-${candidate.id}`}>
          <div className="text-[10px] text-white/60 mb-2 font-medium">Face Lock source image</div>
          <img src={resolveUrl(candidate.face_ref_url || candidate.url)} alt="Face ref"
            className="w-full h-24 object-cover rounded-lg mb-2 bg-black" />
          <input value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste image URL (front-facing, well lit)"
            className="w-full px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[10px] text-white/85 mb-2 focus:border-sky-400/50 outline-none"
            data-testid={`cast-face-ref-input-${candidate.id}`} />
          <div className="flex gap-1.5">
            <button onClick={() => save(url.trim())} disabled={saving || !url.trim()}
              className="flex-1 px-2 py-1 rounded-lg text-[10px] font-semibold bg-sky-500 text-black hover:bg-sky-400 disabled:opacity-40"
              data-testid={`cast-face-ref-save-${candidate.id}`}>
              {saving ? '…' : 'Save'}
            </button>
            <button onClick={() => save('')} disabled={saving || !hasOverride}
              className="flex-1 px-2 py-1 rounded-lg text-[10px] font-medium bg-white/5 text-white/60 hover:bg-white/10 border border-white/10 disabled:opacity-40 flex items-center justify-center gap-1"
              data-testid={`cast-face-ref-reset-${candidate.id}`}>
              <ImageIcon className="w-2.5 h-2.5" /> Use hero
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
